'use client';

import { Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function ThemeToggle() {
    const [darkMode, setDarkMode] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const applyTheme = (isDark: boolean) => {
            setDarkMode(isDark);
            if (isDark) {
                document.documentElement.classList.add('dark');
            } else {
                document.documentElement.classList.remove('dark');
            }
        };

        // Initial check: Prioritize localStorage, then system preference
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            applyTheme(savedTheme === 'dark');
        } else {
            applyTheme(mediaQuery.matches);
        }

        // Listen for system changes
        const handleChange = (e: MediaQueryListEvent) => {
            if (!localStorage.getItem('theme')) {
                applyTheme(e.matches);
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const toggleTheme = () => {
        const newMode = !darkMode;
        setDarkMode(newMode);

        if (newMode) {
            document.documentElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    };

    // Don't render until mounted to prevent hydration mismatch
    if (!mounted) {
        return (
            <button
                className="p-3 rounded-full bg-surface border border-border transition-all duration-200 flex items-center justify-center"
                aria-label="Toggle theme"
                disabled
            >
                <div className="w-[18px] h-[18px]" />
            </button>
        );
    }

    return (
        <button
            onClick={toggleTheme}
            className="p-3 rounded-full bg-surface border border-border hover:border-accent transition-all duration-200 hover:scale-110 flex items-center justify-center"
            aria-label="Toggle theme"
            suppressHydrationWarning
        >
            {darkMode ? (
                <Sun size={18} className="text-foreground" />
            ) : (
                <Moon size={18} className="text-foreground" />
            )}
        </button>
    );
}
